import { analyzeCategory, normalizeCategoryText } from '../../../utils/categoryClassifier';
import { getTimeUnclearLabel } from '../../../utils/timeBucketLabel';
import { buildConciseMilestoneTitle } from '../../../utils/milestoneTitles';
import { normalizeImportCategory } from './importLogsParsingClassification';
import { parseDateForStorage } from './importLogsParsingTime';
import { buildResolvedEntryTitle, isGenericResolvedTitle } from './importLogsCategoryResolutionTitles';
import {
  hasStrongBehaviorActionSignal,
  hasStrongMedicationSignal,
} from './importLogsParsingClassification';
import { resolveFinalImportCategory } from './importLogsFinalCategoryResolution';
import { buildSplitRowsFromSourceBlock, shouldAttemptMultiDomainSplit } from './importLogsMultiDomainSplit';
import {
  LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS,
  LONG_NOTE_ACTIVITY_PATTERNS,
  LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS,
  LONG_NOTE_FOOD_CONSUMPTION_PATTERNS,
  LONG_NOTE_FOOD_CONTEXT_PATTERNS,
  LONG_NOTE_FOOD_PRIMARY_PATTERNS,
  LONG_NOTE_MEDICATION_SIDE_EFFECT_PATTERNS,
  LONG_NOTE_MILESTONE_DOMINANT_PATTERNS,
  LONG_NOTE_MOOD_PATTERNS,
  LONG_NOTE_SLEEP_PATTERNS,
} from './importLogsLongNotePatterns';
import {
  CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN,
  CARE_MEDICATION_IMPORT_NAME_GLOBAL_PATTERN,
} from '../../../shared/careMedicationPatterns';

const getComparableText = (row = {}) => String(
  row?.body
  || row?.note
  || row?.text
  || row?.content
  || row?.sourceText
  || row?.originalText
  || ''
).trim();

const getSourceBlockText = (row = {}) => String(
  row?.rawSourceText
  || row?.metadata?.rawSourceText
  || row?.metadata?.originalRawText
  || row?.rawText
  || row?.metadata?.rawText
  || row?.sourceText
  || row?.originalText
  || getComparableText(row)
  || ''
).trim();

const getDateKey = (row = {}) => {
  if (row?.entryDate) {
    return String(row.entryDate);
  }

  const date = row?.date instanceof Date
    ? row.date
    : (row?.date ? parseDateForStorage(row.date) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toDateString();
};

const getTimeKey = (row = {}) => String(
  row?.localTime
  || row?.timeBucket
  || ''
).trim();

const isTimeUnclear = (row = {}) => {
  const localTime = String(row?.localTime || '').trim();
  const timeSource = String(row?.timeSource || '').trim().toLowerCase();
  return !localTime || localTime === getTimeUnclearLabel() || timeSource === 'missing';
};

const OBSERVATIONAL_CATEGORY_SET = new Set(['sleep', 'food', 'mood', 'medication']);

const isObservationalSummaryBlock = (text = '') => {
  const comparable = normalizeCategoryText(text);
  if (!comparable) {
    return false;
  }

  const hasSummaryCue = /\b(?:seems?|seemed|noticing|noticeably|this week|during daytime|after school|again|more tired|falling asleep|sleeping faster|harder to wake|appetite lower|lower appetite|hungry late|eating very little)\b/i.test(comparable);
  const hasSentenceBoundary = /[.!?]/.test(String(text || ''));
  const hasMedicationAdministration = /\b(?:\d+(?:\.\d+)?\s?mg|given|gave|administered|took|dose|tablet(?:s)?\s+(?:daily|nightly|night|morning|evening|dose|doses)|capsule|pill)\b/i.test(comparable);
  const hasMealAnchor = /\b(?:breakfast|lunch|dinner|snack)\s+(?:was|is)\b/i.test(comparable);
  const hasStrongBehaviorOrActivity = /\b(?:school pickup|teacher reported|incident|hit|pushed|slapped|kicked|yelled|music therapy|art class|recess|group work|followed directions|participated)\b/i.test(comparable);

  return hasSummaryCue && hasSentenceBoundary && !hasMedicationAdministration && !hasMealAnchor && !hasStrongBehaviorOrActivity;
};

const textContains = (outer = '', inner = '') => {
  const outerText = normalizeCategoryText(outer);
  const innerText = normalizeCategoryText(inner);
  if (!outerText || !innerText) {
    return false;
  }

  return outerText.includes(innerText);
};

const shouldResolveDominantCategory = (rows = []) => {
  if (!Array.isArray(rows) || rows.length <= 1) {
    return false;
  }

  const uniqueCategories = new Set(rows.map((row) => normalizeImportCategory(row?.category || 'log')));
  const sharedSourceText = getSourceBlockText(pickRepresentativeRow(rows));
  if (
    uniqueCategories.has('medication')
    && uniqueCategories.has('food')
    && /\bmedication adjustment\b/i.test(sharedSourceText)
    && /\b(?:ate|eating|hungry|breakfast|lunch|dinner|snack)\b/i.test(sharedSourceText)
  ) {
    return false;
  }

  if (uniqueCategories.size <= 1) {
    return false;
  }

  if (!Array.from(uniqueCategories).every((category) => OBSERVATIONAL_CATEGORY_SET.has(category))) {
    return false;
  }

  if (!rows.every((row) => isTimeUnclear(row))) {
    return false;
  }

  const sourceBlockTexts = rows
    .map((row) => getSourceBlockText(row))
    .filter(Boolean);
  if (!sourceBlockTexts.length) {
    return false;
  }

  const largestText = sourceBlockTexts
    .slice()
    .sort((left, right) => right.length - left.length)[0];

  if (!isObservationalSummaryBlock(largestText)) {
    return false;
  }

  const overlaps = rows.some((row) => {
    const rowText = getComparableText(row);
    return rowText && largestText && largestText !== rowText && textContains(largestText, rowText);
  });

  return overlaps || uniqueCategories.size > 1;
};

const pickRepresentativeRow = (rows = []) => rows
  .slice()
  .sort((left, right) => getComparableText(right).length - getComparableText(left).length)[0] || null;

const multiDomainSplitHelpers = {
  getComparableText,
  getSourceBlockText,
  pickRepresentativeRow,
  textContains,
};

const titleCase = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const hasStrongLongNoteFoodSignal = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return false;
  }

  const hasConsumption = LONG_NOTE_FOOD_CONSUMPTION_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasFoodContext = LONG_NOTE_FOOD_CONTEXT_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasMealHeader = /\b(?:breakfast|lunch|dinner|snack|meal)\b\s*(?:was|:)/i.test(comparable);

  if (hasMealHeader) return true;
  if (hasConsumption && hasFoodContext) return true;
  return hasConsumption && /\b(?:before school|before leaving for school|after breakfast|after lunch|after dinner|at school pickup)\b/i.test(comparable);
};

const getRowSemanticText = (row = {}) => String(
  row?.body
  || row?.note
  || row?.text
  || row?.content
  || row?.sourceText
  || row?.originalText
  || row?.title
  || ''
).trim();

const extractSemanticEntity = (category = '', text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) return '';

  switch (String(category || '').toLowerCase()) {
    case 'medication': {
      const medicationNames = comparable.match(CARE_MEDICATION_IMPORT_NAME_GLOBAL_PATTERN) || [];
      const doseMatch = comparable.match(CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN);
      const sideEffectMatch = comparable.match(/\b(?:side effects?|appetite|reduced appetite|nausea|sleepy|drowsy|calmer|no concerns|no issues|no problems)\b/i);
      return [
        ...medicationNames.slice(0, 2).map((item) => titleCase(item)),
        doseMatch?.[0] || '',
        sideEffectMatch?.[0] || '',
      ].filter(Boolean).join(' ');
    }
    case 'food': {
      const mealAnchor = [
        /\bbreakfast\b/i.test(comparable) ? 'Breakfast' : '',
        /\blunch\b/i.test(comparable) ? 'Lunch' : '',
        /\bdinner\b/i.test(comparable) ? 'Dinner' : '',
        /\bsnack\b/i.test(comparable) ? 'Snack' : '',
        /\bwater bottle\b/i.test(comparable) ? 'Water bottle' : '',
      ].find(Boolean);
      if (mealAnchor) return mealAnchor;
      const foods = comparable.match(/\b(?:eggs?|strawberries?|bagel|cream cheese|rice|chicken|yogurt|cucumber|juice|crackers|chips|apple|pasta|macaroni|nuggets|granola bar|milk|oatmeal|bananas?|blueberries?)\b/ig) || [];
      return foods.slice(0, 3).map((item) => titleCase(item)).join(' ');
    }
    case 'sleep':
      if (/\bfell asleep\b/i.test(comparable)) return 'Fell asleep';
      if (/\bwoke up\b/i.test(comparable) || /\bwoke\b/i.test(comparable)) return 'Wake';
      if (/\bbedtime\b/i.test(comparable)) return 'Bedtime';
      if (/\bnap\b/i.test(comparable)) return 'Nap';
      return '';
    case 'activity': {
      const activityAnchor = [
        /\bart class\b/i.test(comparable) ? 'Art class' : '',
        /\bmusic therapy\b/i.test(comparable) ? 'Music therapy' : '',
        /\bgroup painting\b/i.test(comparable) ? 'Group painting' : '',
        /\brecess\b/i.test(comparable) ? 'Recess' : '',
        /\bexercise\b/i.test(comparable) ? 'Exercise' : '',
        /\bgroup work\b/i.test(comparable) ? 'Group work' : '',
        /\bproject\b/i.test(comparable) ? 'Project' : '',
        /\bparticipat(?:ed|ing)\b/i.test(comparable) ? 'Participation' : '',
        /\bfocused\b/i.test(comparable) ? 'Focused work' : '',
        /\bindependent(?:ly)?\b/i.test(comparable) ? 'Independent work' : '',
      ].find(Boolean);
      return activityAnchor || '';
    }
    case 'behavior':
      if (/\bno aggressive behavior(?:s)?\b/i.test(comparable) || /\bno aggression\b/i.test(comparable)) return 'No aggressive behaviors';
      if (/\btransition(?:ing)?\b/i.test(comparable) || /\bfrustrat/i.test(comparable)) return 'Transition frustration';
      if (/\b(?:incident|escalat(?:ed|ion)|yell(?:ed|ing)?|push(?:ed|ing)?|hit(?:ting)?|kick(?:ed|ing)?|redirect(?:ed|ion)?|calm corner|deep breathing|sensory squeezes?|overstimulated)\b/i.test(comparable)) return 'Behavior incident';
      return '';
    case 'mood': {
      const moodMatch = comparable.match(/\b(happy|affectionate|tired|anxious|worried|excited|calm|frustrated|upset|sad|content|nervous|proud|mixed|overstimulated|more relaxed)\b/i);
      return moodMatch?.[1] ? titleCase(moodMatch[1]) : '';
    }
    case 'milestone':
      return buildConciseMilestoneTitle(comparable);
    default:
      return '';
  }
};

const getSemanticAnchorScore = (text = '') => {
  const comparable = String(text || '').trim();
  const scores = {
    medication: 0,
    behavior: 0,
    activity: 0,
    food: 0,
    mood: 0,
    sleep: 0,
    milestone: 0,
  };

  if (!comparable) return { anchor: null, scores };
  if (hasStrongMedicationSignal(comparable)) scores.medication += 6;
  if (LONG_NOTE_MEDICATION_SIDE_EFFECT_PATTERNS.some((pattern) => pattern.test(comparable))) scores.medication += 3;
  if (LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable)) || hasStrongBehaviorActionSignal(comparable)) scores.behavior += 8;
  if (LONG_NOTE_ACTIVITY_PATTERNS.some((pattern) => pattern.test(comparable)) || LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) scores.activity += 6;
  if (hasStrongLongNoteFoodSignal(comparable) || LONG_NOTE_FOOD_PRIMARY_PATTERNS.some((pattern) => pattern.test(comparable))) scores.food += 6;
  if (LONG_NOTE_MOOD_PATTERNS.some((pattern) => pattern.test(comparable))) scores.mood += 4;
  if (LONG_NOTE_SLEEP_PATTERNS.some((pattern) => pattern.test(comparable))) scores.sleep += 6;
  if (LONG_NOTE_MILESTONE_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) scores.milestone += 5;
  if (/\b(?:therapy|exercise|tolerat(?:ed|ing)|followed directions|focused|group work|project|improv(?:ed|ement)|progress|independent(?:ly)?)\b/i.test(comparable)) scores.activity += 2;
  if (/\b(?:reduced appetite|no appetite|less appetite|appetite change|side effect|side effects|nausea|stomach)\b/i.test(comparable)) {
    scores.medication += 2;
    scores.food = Math.max(0, scores.food - 2);
  }
  if (/\b(?:asleep|fell asleep|woke up|bedtime|nap)\b/i.test(comparable)) scores.sleep += 1;

  const ranking = ['medication', 'behavior', 'activity', 'food', 'sleep', 'mood', 'milestone'];
  const anchor = ranking.reduce((best, category) => (
    scores[category] > (scores[best] || 0) ? category : best
  ), ranking[0]);

  if (!anchor || (scores[anchor] || 0) <= 0) return { anchor: null, scores };
  return { anchor, scores };
};

const getLongNoteSemanticProfile = (row = {}) => {
  const text = getRowSemanticText(row);
  const title = String(row?.title || row?.titlePrefix || row?.titleHint || '').trim();
  const category = String(row?.category || row?.categoryHint || 'log').trim().toLowerCase();
  const { anchor, scores } = getSemanticAnchorScore(text);
  const semanticTitle = extractSemanticEntity(anchor || category, `${title} ${text}`.trim());
  const semanticGeneric = isGenericResolvedTitle(title, category) && !semanticTitle;
  const semanticRichnessScore = [
    semanticTitle ? 3 : 0,
    /\b(?:\d+(?:\.\d+)?\s?(?:mg|ml|mcg|g|units?)|guanfacine|melatonin|clonidine|sertraline|appetite|side effect|transition|frustrat|yell|push|calm corner|deep breathing|art class|recess|therapy|exercise|progress|improv|independent|sleep|asleep|woke|ate|drank)\b/i.test(text) ? 3 : 0,
    text.split(/\s+/).filter(Boolean).length >= 8 ? 1 : 0,
    title && !isGenericResolvedTitle(title, category) ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);

  return {
    semanticAnchor: anchor || category || 'log',
    semanticSignature: semanticGeneric
      ? ''
      : `${anchor || category || 'log'}|${semanticTitle || titleCase(text.split(/(?<=[.!?])\s+/)[0] || '') || ''}`.trim(),
    semanticGeneric,
    semanticRichnessScore,
    semanticScores: scores,
  };
};

export const suppressOverlappingCandidateRows = (rows = []) => {
  const longNoteRows = Array.isArray(rows) ? rows : [];
  const annotatedRows = longNoteRows.map((row, index) => {
    const semantic = getLongNoteSemanticProfile(row);
    return {
      ...row,
      ...semantic,
      metadata: {
        ...(row.metadata || {}),
        ...semantic,
        longNoteMode: true,
        longNoteIndex: index,
      },
    };
  });

  const groups = new Map();
  annotatedRows.forEach((row, index) => {
    if (!row?.metadata?.longNoteMode) {
      return;
    }
    const paragraphIndex = row?.metadata?.paragraphIndex ?? 'na';
    const timeKey = String(row?.localTime || row?.timeBucket || '').trim() || 'na';
    const category = String(row?.category || 'log').trim().toLowerCase();
    const anchor = String(row?.semanticAnchor || '').trim() || category;
    const key = `${paragraphIndex}|${timeKey}|${category}|${anchor}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push({ row, index });
  });

  const suppressed = new Set();
  groups.forEach((items) => {
    if (items.length < 2) return;
    const richItems = items.filter(({ row }) => !row.semanticGeneric && String(row.semanticSignature || '').trim());

    if (richItems.length > 0) {
      items.forEach(({ row, index }) => {
        if (row.semanticGeneric || !String(row.semanticSignature || '').trim()) suppressed.add(index);
      });
    }

    const signatureBuckets = new Map();
    richItems.forEach((item) => {
      const signatureKey = String(item.row.semanticSignature || '').trim();
      if (!signatureBuckets.has(signatureKey)) signatureBuckets.set(signatureKey, []);
      signatureBuckets.get(signatureKey).push(item);
    });

    signatureBuckets.forEach((signatureItems) => {
      if (signatureItems.length < 2) return;
      const ranked = [...signatureItems].sort((left, right) => {
        const leftScore = (left.row.semanticRichnessScore || 0) + (left.row.semanticSignature ? 1 : 0);
        const rightScore = (right.row.semanticRichnessScore || 0) + (right.row.semanticSignature ? 1 : 0);
        if (leftScore !== rightScore) return rightScore - leftScore;
        return left.index - right.index;
      });

      const keeper = ranked[0]?.row || null;
      signatureItems.forEach(({ row, index }) => {
        if (row !== keeper && (row.semanticRichnessScore || 0) <= (keeper?.semanticRichnessScore || 0)) {
          suppressed.add(index);
        }
      });
    });
  });

  return annotatedRows.filter((row, index) => !suppressed.has(index));
};

const applyResolvedSemantics = (rows = []) => rows.map((row) => {
  const text = getComparableText(row);
  const resolvedCategory = normalizeImportCategory(resolveFinalImportCategory({
    ...row,
    sourceMode: row?.sourceMode || row?.metadata?.sourceMode || '',
  }) || row?.category || 'log');
  const titleHintCategory = normalizeImportCategory(
    row?.metadata?.aiOriginalCategoryHint
    || row?.categoryHint
    || row?.category
    || 'log'
  );
  const shouldHonorTitleHint = titleHintCategory === resolvedCategory;
  const resolvedTitle = buildResolvedEntryTitle(resolvedCategory, text, {
    titleHint: shouldHonorTitleHint
      ? (row?.titleHint || row?.metadata?.titleHint || row?.title || row?.titlePrefix || '')
      : '',
  });

  return {
    ...row,
    category: resolvedCategory,
    title: resolvedTitle,
    titlePrefix: resolvedTitle,
    parsedSourceCategory: row?.parsedSourceCategory || row?.categoryHint || row?.category || resolvedCategory,
    metadata: {
      ...(row?.metadata || {}),
      ...(row?.metadata?.aiOriginalCategoryHint
        ? { aiResolvedCategory: resolvedCategory }
        : {}),
      finalResolvedCategory: resolvedCategory,
      finalResolvedTitle: resolvedTitle,
    },
  };
});

const pickDominantCategory = (rows = []) => {
  const representative = pickRepresentativeRow(rows);
  const sourceBlockText = getSourceBlockText(representative);
  const comparable = normalizeCategoryText(sourceBlockText);
  const categories = new Set(rows.map((row) => normalizeImportCategory(row?.category || 'log')));

  if (categories.has('sleep') && /\b(?:falling asleep|fell asleep|sleep|asleep|bedtime|woke|waking)\b/i.test(comparable)) {
    return 'sleep';
  }

  if (categories.has('medication') && /\b(?:medication adjustment|side effects?|appetite lower after medication|sleeping faster|harder to wake)\b/i.test(comparable)) {
    return 'medication';
  }

  if (categories.has('food') && /\b(?:appetite|hungry|eating very little|ate very little|not eating)\b/i.test(comparable) && !categories.has('sleep')) {
    return 'food';
  }

  const analysis = analyzeCategory(sourceBlockText, { allowUnknown: true });
  const proposed = normalizeImportCategory(analysis?.category || representative?.category || 'log');

  if (proposed && proposed !== 'unknown' && proposed !== 'log') {
    return proposed;
  }

  return normalizeImportCategory(representative?.category || 'log');
};

const buildResolvedRow = (rows = []) => {
  const representative = pickRepresentativeRow(rows);
  if (!representative) {
    return null;
  }

  const dominantCategory = pickDominantCategory(rows);
  const dominantMatch = rows
    .filter((row) => normalizeImportCategory(row?.category || 'log') === dominantCategory)
    .sort((left, right) => getComparableText(right).length - getComparableText(left).length)[0] || representative;

  const sourceBlockText = getSourceBlockText(representative);
  const suppressedCategories = Array.from(new Set(
    rows
      .map((row) => normalizeImportCategory(row?.category || 'log'))
      .filter((category) => category && category !== dominantCategory)
  ));

  return {
    ...dominantMatch,
    category: dominantCategory,
    body: sourceBlockText || dominantMatch.body || dominantMatch.note || dominantMatch.text || '',
    note: sourceBlockText || dominantMatch.note || dominantMatch.body || dominantMatch.text || '',
    text: sourceBlockText || dominantMatch.text || dominantMatch.note || dominantMatch.body || '',
    content: sourceBlockText || dominantMatch.content || dominantMatch.note || dominantMatch.body || '',
    details: sourceBlockText || dominantMatch.details || dominantMatch.note || dominantMatch.body || '',
    description: sourceBlockText || dominantMatch.description || dominantMatch.note || dominantMatch.body || '',
    summary: sourceBlockText || dominantMatch.summary || dominantMatch.note || dominantMatch.body || '',
    value: sourceBlockText || dominantMatch.value || dominantMatch.note || dominantMatch.body || '',
    sourceText: sourceBlockText || dominantMatch.sourceText || '',
    originalText: sourceBlockText || dominantMatch.originalText || '',
    rawText: sourceBlockText || dominantMatch.rawText || '',
    rawSourceText: sourceBlockText || dominantMatch.rawSourceText || '',
    metadata: {
      ...(dominantMatch.metadata || {}),
      dominantCategoryResolution: {
        applied: true,
        dominantCategory,
        suppressedCategories,
        competingCategories: Array.from(new Set(rows.map((row) => normalizeImportCategory(row?.category || 'log')))),
      },
    },
  };
};

export const resolveDominantCategoryOverlaps = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return Array.isArray(rows) ? rows : [];
  }

  const candidateRows = suppressOverlappingCandidateRows(rows);

  const groupedRows = new Map();

  candidateRows.forEach((row) => {
    const sourceBlockText = getSourceBlockText(row);
    const dateKey = getDateKey(row);
    const timeKey = getTimeKey(row) || 'time-unclear';
    const key = `${normalizeCategoryText(sourceBlockText)}||${dateKey}||${timeKey}`;
    const bucket = groupedRows.get(key) || [];
    bucket.push(row);
    groupedRows.set(key, bucket);
  });

  const resolved = [];
  groupedRows.forEach((group) => {
    if (shouldResolveDominantCategory(group)) {
      const row = buildResolvedRow(group);
      if (row) {
        resolved.push(row);
      }
      return;
    }

    if (shouldAttemptMultiDomainSplit(group, multiDomainSplitHelpers)) {
      resolved.push(...buildSplitRowsFromSourceBlock(group, multiDomainSplitHelpers));
      return;
    }

    resolved.push(...group);
  });

  return applyResolvedSemantics(resolved);
};
